/**
 * PF-3b (fieldiq-api#1224) — the review of an AI-read ILI anomaly list.
 *
 * The API's parse returns every proposed row with the `status` the confirm
 * would give it. Only `ready` rows may be saved: a refused row would fail the
 * confirm's all-or-nothing validation, and a duplicate / conflict / repeat
 * would be skipped by it anyway. So the review offers a checkbox on `ready`
 * rows only, and every other status says in words that the row will not be
 * saved and why.
 *
 * Nothing here recomputes a status or a count — they are the API's.
 */
import type { PfAnomalyImportRow, PfAnomalyParseRow } from '../types/pf-digs';

/** The one status a reviewer may select. Anything else — including a status this file does not know — is not saveable. */
export const parseRowSelectable = (status: string | null | undefined): boolean => status === 'ready';

/** What each status means to the reviewer. An unknown status says so rather than reading as ready. */
export function parseStatusLabel(r: Pick<PfAnomalyParseRow, 'status' | 'firstIndex'>, refOf: (index: number) => string | null): string {
  switch (r.status) {
    case 'ready':
      return 'Ready to save';
    case 'refused':
      return 'Refused — will not be saved';
    case 'duplicate':
      return 'Already on this site — will not be saved';
    case 'feature_id_conflict':
      return 'Feature ID already on this site at a different position — will not be saved; check which is right';
    case 'duplicate_in_batch': {
      const first = typeof r.firstIndex === 'number' ? refOf(r.firstIndex) : null;
      return `Repeats an earlier row of this list${first ? ` (${first})` : ''} — will not be saved`;
    }
    default:
      return `Not saveable (${String(r.status)})`;
  }
}

/** A cell of a proposed row: an absent value is `—`, a real 0 stays 0. */
export function parseCell(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '—';
  const s = String(v).trim();
  return s === '' ? '—' : s;
}

/** The editable fields of a proposed row, in table order. */
export const PARSE_FIELDS: { key: keyof PfAnomalyImportRow; label: string; kind: 'text' | 'number' | 'type'; unit?: 'chainage' | 'wt' }[] = [
  { key: 'feature_id', label: 'Feature ID', kind: 'text' },
  { key: 'feature_type', label: 'Type', kind: 'type' },
  { key: 'chainage', label: 'Chainage', kind: 'number', unit: 'chainage' },
  { key: 'odometer', label: 'Odometer', kind: 'number', unit: 'chainage' },
  { key: 'clock_position', label: 'Clock', kind: 'text' },
  { key: 'depth_pct_wt', label: 'Depth % WT', kind: 'number' },
  { key: 'length', label: 'Length', kind: 'number', unit: 'wt' },
  { key: 'width', label: 'Width', kind: 'number', unit: 'wt' },
  { key: 'priority', label: 'Priority', kind: 'text' },
  { key: 'notes', label: 'Notes', kind: 'text' },
];

/**
 * Apply a reviewer's edit (input strings) to a proposed row. A blank input is
 * absent (null), never 0. Returns the field names that are not numbers, so the
 * edit is refused in place rather than sent.
 */
export function applyParseEdit(
  row: PfAnomalyImportRow,
  inputs: Record<string, string>,
): { row: PfAnomalyImportRow; invalid: string[] } {
  const out: PfAnomalyImportRow = { ...row };
  const set = out as unknown as Record<string, unknown>;
  const invalid: string[] = [];
  PARSE_FIELDS.forEach((f) => {
    if (!(f.key in inputs)) return;
    const t = inputs[f.key].trim();
    if (f.kind === 'number') {
      if (t === '') set[f.key] = null;
      else if (Number.isFinite(Number(t))) set[f.key] = Number(t);
      else invalid.push(f.label);
    } else if (f.kind === 'type') {
      out.feature_type = t;
    } else {
      set[f.key] = t === '' ? null : t;
    }
  });
  return { row: out, invalid };
}

/**
 * The confirm validator's field names, in the words the review table uses.
 * Its refusals arrive as "rows[7].depth_pct_wt must be a number between 0 and
 * 100" (parseAnomaly in fieldiq-api src/lib/pf-anomalies.ts); the reviewer
 * should read the field as they know it and see what the list said.
 */
export const PARSE_ERROR_FIELD_LABELS: Record<string, string> = {
  feature_id: 'Feature ID',
  segment_id: 'Segment',
  chainage: 'Chainage',
  odometer: 'Odometer',
  latitude: 'Latitude',
  longitude: 'Longitude',
  clock_position: 'Clock position',
  feature_type: 'Feature type',
  depth_pct_wt: 'Depth (% WT)',
  length: 'Length',
  width: 'Width',
  ili_vendor: 'ILI vendor',
  ili_run_date: 'ILI run date',
  priority: 'Priority',
  notes: 'Notes',
};

/**
 * One validator refusal as a plain sentence: "Depth (% WT) must be between 0
 * and 100 — the list says 112". Anything not in the validator's shape, or naming
 * a field not mapped above, is returned exactly as the API sent it.
 */
export function parseErrorSentence(raw: string, row: Record<string, unknown> | null | undefined): string {
  const m = /^(?:rows\[\d+\]\.)?([a-z_]+) (must .+|is required.*)$/.exec(raw.trim());
  if (!m) return raw;
  const label = PARSE_ERROR_FIELD_LABELS[m[1]];
  if (!label) return raw;
  const rest = m[2].replace(/^must be a number between /, 'must be between ');
  const v = row ? row[m[1]] : undefined;
  const said = v === null || v === undefined || String(v).trim() === '' ? '' : ` — the list says ${String(v)}`;
  return `${label} ${rest}${said}`;
}
